import { Trans, useTranslation } from 'react-i18next';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { IHiMessageData } from '@/views/Chats/types';
import {
  getUserNameByUserInfo,
  numFormat,
  convertOfficialIdToHiId,
  alignSystemAccountId,
} from '@/utils';
import style from './index.module.css';
import { Textarea, Button, HelperTooltip, Popup, Loading } from 'components';
import { OfferModal } from '../OfferModal';
import {
  useSwitch,
  useOperateHiChatDraft,
  usePersistedHiChatDraft,
  useHiUserCache,
  OfficialAccountVerifyStatus,
  useWindowVisible,
  useCommonImageUpload,
  useCurrentAddressInfo,
} from '@/hooks';
import {
  IconRcHiInputExtend,
  IconRcHiCloseModal,
  IconRcHiSendMsg,
  IconRcHiButtonLoading,
  IconHiInfo,
  IconHiOfferBounty,
} from 'assets/hi';
import { useAddMessage, usePostMessageTyping } from '../../hooks/hiMessage';
import { useHiFreeModal } from '../../hooks/useHiFreeModal';
import type { RcFile } from 'antd/es/upload';
import { IconRcCloseIcon } from '@/assets/editor';
import { UPLOAD_SIZE_LIMIT } from '@/consts';
import Modal from 'react-modal';
import cx from 'clsx';
import message from 'antd/es/message';
import { useHiMessagePermission, MessagePermissionLevel } from '../../hooks/hiMessagePermission';
import { formatOfferPriceNumber } from '../../utils';
import { MessageInputToolbar } from './components/MessageInputToolbar';
import { suicideToast } from '@/components/SystemErrorToast';

const INPUT_LINE_HEIGHT = 24;
const MAX_INPUT_LINE = 12;
const MIN_INPUT_LINE = 5;
const DEBANK_HI_ID = '0a110133';

export const MessageInput = (props: {
  parentElementId: string;
  sessionId?: string | number;
  price?: number;
  isLoading: boolean;
  otherSideUserCardInfo?: any;
  officialAccountInfo?: OfficialAccount;
  systemAccountInfo?: SystemAccount;
  socketConnected?: boolean;
  refetchHiUserInfo: () => void;
  minInputLineCount?: number;
  onScrollHeightChange: (height: number) => void;
  openInModal?: boolean;
  initialText?: string;
}) => {
  const { t } = useTranslation();

  const {
    parentElementId,
    sessionId,
    price = 0,
    isLoading,
    otherSideUserCardInfo,
    officialAccountInfo,
    systemAccountInfo,
    socketConnected,
    minInputLineCount = MIN_INPUT_LINE,
    onScrollHeightChange,
    refetchHiUserInfo,
    openInModal,
    initialText = '',
  } = props;
  const [value, setValue] = useState('');
  const { on, turnOff, toggle } = useSwitch(false);
  const { userInfo } = useCurrentAddressInfo();
  const { hideHiFreeModal: hideHiFreeModalOption } = useHiFreeModal();
  const {
    on: onOfferModal,
    turnOff: turnOffOfferModal,
    turnOn: turnOnOfferModal,
  } = useSwitch(false);
  const { putDraftOnSession } = useOperateHiChatDraft();
  const draft = usePersistedHiChatDraft(sessionId);
  const [initialDraft, seInitialDraft] = useState(false);
  const { userDict } = useHiUserCache();
  const isVisible = useWindowVisible();
  const isUploadingRef = useRef(false);

  const hideHiFreeModal = useMemo(
    () => !!systemAccountInfo || !!officialAccountInfo || hideHiFreeModalOption,
    [hideHiFreeModalOption, officialAccountInfo, systemAccountInfo],
  );

  const systemAccountInfoId = useMemo(
    () => systemAccountInfo?.id && alignSystemAccountId(systemAccountInfo.id),
    [systemAccountInfo?.id],
  );

  const { mutateAsync: uploadImage, isLoading: isUploading } = useCommonImageUpload('hi');

  const {
    on: isSendingMessage,
    turnOn: turnOnSendingMessage,
    turnOff: turnOffSendingMessage,
  } = useSwitch(false);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const inputHelperRef = useRef<HTMLTextAreaElement>(null);
  const inputModalRef = useRef<HTMLTextAreaElement>(null);

  const otherSideAddrInfo = useMemo(
    () =>
      (userDict[otherSideUserCardInfo?.userInfo?.id] as UserInfo) ||
      otherSideUserCardInfo?.userInfo,
    [userDict, otherSideUserCardInfo],
  );

  const { mutateAsync: messageTyping } = usePostMessageTyping();
  const [selectedFile, setSelectedFile] = useState<{
    file: any;
    id?: string;
    url?: string;
  } | null>();
  const isInputFocus = useCallback(() => {
    const activeEl = document.activeElement;
    return activeEl && (activeEl === inputRef.current || activeEl === inputModalRef.current);
  }, []);

  const { mutateAsync: mutateAsyncAddMessage } = useAddMessage();

  const otherSideHiId = useMemo(
    () =>
      (systemAccountInfoId ||
        otherSideAddrInfo?.id ||
        convertOfficialIdToHiId(officialAccountInfo?.id!)) as string,
    [officialAccountInfo?.id, otherSideAddrInfo?.id, systemAccountInfoId],
  );

  useEffect(() => {
    const emitHiTyping = () => {
      isVisible && isInputFocus() && messageTyping(otherSideHiId);
    };
    const timer = setInterval(emitHiTyping, 1000);
    return () => {
      clearInterval(timer);
    };
  }, [isInputFocus, isVisible, messageTyping, otherSideHiId]);

  const {
    isLoading: messagePermissionLoading,
    permission: messagePermission,
    refetch: refetchMessagePermission,
  } = useHiMessagePermission(otherSideHiId, !socketConnected);
  const isFree = [MessagePermissionLevel.free, MessagePermissionLevel.restricted].includes(
    messagePermission?.level,
  );

  const otherSideAccountName = useMemo(() => {
    const officialId =
      officialAccountInfo?.verify_status === OfficialAccountVerifyStatus.VERIFIED
        ? officialAccountInfo?.name
        : officialAccountInfo?.id;
    return systemAccountInfo?.name || officialId || getUserNameByUserInfo(otherSideAddrInfo);
  }, [officialAccountInfo, otherSideAddrInfo, systemAccountInfo?.name]);

  const clearImage = useCallback(() => {
    setSelectedFile(undefined);
    isUploadingRef.current = false;
  }, []);

  const resetState = useCallback(
    (clear?: boolean) => {
      setValue(clear ? '' : initialText);
      turnOff();
      turnOffOfferModal();
      clearImage();
    },
    [clearImage, turnOff, turnOffOfferModal, initialText],
  );

  useEffect(() => {
    seInitialDraft(false);
    resetState();
    inputRef?.current?.focus();
  }, [turnOff, sessionId, turnOffOfferModal, resetState]);

  // load draft
  useEffect(() => {
    if (initialDraft || initialText) {
      return;
    }
    if (!value && !isSendingMessage) {
      draft && setValue(draft);
      seInitialDraft(true);
    }
  }, [draft, initialDraft, isSendingMessage, sessionId, value, initialText]);

  const toggleInputModal = useCallback(() => {
    const ref = on ? inputRef : inputModalRef;

    toggle();

    setTimeout(() => {
      ref?.current?.focus();
    });
  }, [on, toggle]);

  const sendMessage = useCallback(
    async (data?: IHiMessageData, tx?: any) => {
      const addMessageData = {
        toUserId: otherSideHiId,
        data,
        bonus: messagePermission?.offer_bonus,
        tx,
      };
      return await mutateAsyncAddMessage(addMessageData);
    },
    [messagePermission?.offer_bonus, mutateAsyncAddMessage, otherSideHiId],
  );

  const onSubmitMessage = useCallback(
    async (tx?: any) => {
      turnOnSendingMessage();
      return await sendMessage(
        {
          type: 'text',
          value: {
            version: 'v1',
            data: value,
            image: selectedFile?.url ? selectedFile?.url : undefined,
          },
        },

        // mock text message with image and url

        // {
        //   type: 'text',
        //   value: {
        //     version: 'v1',
        //     data: value,
        //     image:
        //       'https://static.debank.com/image/user/hi_image/0x5853ed4f26a3fcea565b3fbc698bb19cdf6deb85/fbbbe3c2e6b90094534ab78a7acbf249.png',
        //     url: 'www.debank.com',
        //   },
        // },
        tx,
      )
        .then(() => {
          resetState(true);
          sessionId && putDraftOnSession(sessionId, '');
          return true;
        })
        .catch((errStr: string) => {
          console.warn('submit offer err', errStr);
          if (errStr?.includes('attention price has changed')) {
            message.info(
              t('MessageInput_MessageInput.priceChanged', { name: otherSideAccountName }),
              4,
            );
            return false;
          }
          if (errStr?.includes('Address that may have initiated a sybil attack')) {
            message.error(errStr, 4);
            return false;
          }
          message.info(errStr);
          throw new Error(`send offer error: ${errStr.toString()}`);
        })
        .finally(() => {
          turnOffSendingMessage();
          refetchHiUserInfo();
          refetchMessagePermission();
        });
    },
    [
      turnOnSendingMessage,
      sendMessage,
      value,
      selectedFile?.url,
      resetState,
      sessionId,
      putDraftOnSession,
      otherSideAccountName,
      turnOffSendingMessage,
      refetchHiUserInfo,
      refetchMessagePermission,
      t,
    ],
  );
  const handleSubmitButtonClick = useCallback(() => {
    if (userInfo?.is_suicide && otherSideHiId !== DEBANK_HI_ID) {
      suicideToast();
      return;
    }
    if (isFree && hideHiFreeModal) {
      onSubmitMessage();
      return;
    }
    turnOnOfferModal();
  }, [
    hideHiFreeModal,
    isFree,
    onSubmitMessage,
    otherSideHiId,
    turnOnOfferModal,
    userInfo?.is_suicide,
  ]);

  const handleInputChange = useCallback(
    (value: string) => {
      setValue(value || '');
      if (!isSendingMessage && sessionId && value !== draft) {
        putDraftOnSession(sessionId, value);
      }
    },
    [draft, isSendingMessage, putDraftOnSession, sessionId],
  );

  const isEmptyContent = useMemo(
    () => !value.length && !selectedFile?.url,
    [selectedFile?.url, value.length],
  );

  const buttonForbidden = messagePermission?.level === MessagePermissionLevel.forbidden;
  const SubmitButtonComponent = (
    <Button
      className={cx(
        style.submitButton,
        isFree && !isLoading ? style.isFreeButton : null,
        buttonForbidden && style.disabledButton,
        isLoading ? style.isLoading : null,
      )}
      disabled={isEmptyContent || isUploading || buttonForbidden}
      type={'primary'}
      onClick={handleSubmitButtonClick}>
      {isLoading || messagePermissionLoading ? (
        <IconRcHiButtonLoading className="loadingIcon" />
      ) : buttonForbidden ? (
        ''
      ) : isFree ? (
        t('MessageInput_MessageInput.free')
      ) : (
        `$${numFormat(price, 2)}`
      )}
      <IconRcHiSendMsg className={style.submitIcon} />
    </Button>
  );

  const ToolbarBottomTip =
    !messagePermissionLoading && !!messagePermission ? (
      <div className={style.toolbarBottomTip}>
        {messagePermission.offer_bonus && !isFree ? (
          <HelperTooltip
            title={t('MessageInput_MessageInput.return', {
              bonus: formatOfferPriceNumber(messagePermission.offer_bonus, '$'),
            })}>
            <div className={style.bountyTip}>
              <IconHiOfferBounty className={style.bountyIcon} />
              {t('MessageInput_MessageInput.1hwnafj')}{' '}
              <span className={style.bountyAmount}>
                {formatOfferPriceNumber(messagePermission.offer_bonus, '$')}
              </span>
            </div>
          </HelperTooltip>
        ) : null}
        <div className={style.messageTip}>
          {isFree ? (
            <Popup
              on="hover"
              mouseEnterDelay={500}
              overlayStyle={{ width: 384 }}
              arrow={false}
              position={['top center']}
              className="messageTipOverlay"
              offsetY={10}
              trigger={<IconHiInfo style={{ marginRight: 4 }} />}>
              <ul className={style.iconTip}>
                <li>{t('MessageInput_MessageInput.4rbua5')}</li>
                <li>{t('MessageInput_MessageInput.aoocdp')}</li>
                <li>{t('MessageInput_MessageInput.1nko721')}</li>
              </ul>
            </Popup>
          ) : null}
          <span className={style.tipInfo}>
            {messagePermission?.level === MessagePermissionLevel.restricted ? (
              <span>
                <Trans
                  i18nKey="MessageInput_MessageInput.freeMsgBeforeFollow"
                  values={{ amount: messagePermission?.free_message_count }}>
                  Send
                  <span className={style.freeMessageCountText}>
                    {messagePermission?.free_message_count}
                  </span>{' '}
                  free messages before they reply or follow you
                </Trans>
              </span>
            ) : (
              messagePermission?.message
            )}
          </span>
        </div>
      </div>
    ) : (
      <div className={style.toolbarBottomTip}>
        <span className={style.tipInfo}>&nbsp;</span>
      </div>
    );

  const messageInputPlaceholder = useMemo(() => {
    return otherSideAccountName
      ? t('MessageInput_MessageInput.inputHolder', { name: otherSideAccountName })
      : '';
  }, [otherSideAccountName, t]);

  // message input height change
  useEffect(() => {
    setTextContentHeight(inputHelperRef.current?.scrollHeight || 0);
  }, [inputHelperRef?.current?.scrollHeight]);
  const [textContentHeight, setTextContentHeight] = useState(INPUT_LINE_HEIGHT);

  const messageInputLineCount = useMemo(() => {
    const realLine = Math.ceil(textContentHeight / INPUT_LINE_HEIGHT) + 1;
    return Math.min(Math.max(minInputLineCount, realLine), MAX_INPUT_LINE);
  }, [minInputLineCount, textContentHeight]);

  useEffect(() => {
    onScrollHeightChange(messageInputLineCount * INPUT_LINE_HEIGHT);
  }, [messageInputLineCount, onScrollHeightChange]);

  const getContainerWrapper = useCallback(
    () => (document.querySelector(`#${parentElementId}`) as HTMLElement) || document.body,
    [parentElementId],
  );

  // https://stackoverflow.com/questions/66261433/add-emojis-to-input
  const onEmojiClick = useCallback(
    (emoji: string) => {
      const activeInput = on ? inputModalRef : inputRef;
      const cursor = activeInput.current?.selectionStart || 0;
      const text = value.slice(0, cursor) + emoji + value.slice(cursor);
      handleInputChange(text);
      const newCursor = cursor + emoji.length;
      setTimeout(() => {
        activeInput.current?.setSelectionRange(newCursor, newCursor);
        activeInput.current?.focus();
      }, 10);
    },
    [handleInputChange, on, value],
  );

  const handleBeforeUpload = useCallback(
    (file: RcFile) => {
      return new Promise<void>(async (resolve, reject) => {
        if (file.size > UPLOAD_SIZE_LIMIT) {
          message.error(t('UserBanner.sizeLimit'));
          return reject();
        }
        setSelectedFile({ file });
        isUploadingRef.current = true;
        const imageRes: any = await uploadImage(file).catch(e => {
          message.warn(e);
          clearImage();
        });
        if (imageRes?.image?.id && isUploadingRef.current) {
          setSelectedFile({ file, id: imageRes?.image?.id, url: imageRes?.image?.raw?.full_url });
        }
        isUploadingRef.current = false;
      });
    },
    [clearImage, uploadImage, t],
  );

  const selectedFileUrl = useMemo(
    () => (selectedFile?.file ? window.URL.createObjectURL(selectedFile?.file) : ''),
    [selectedFile],
  );

  const ImagePreviewComp = useMemo(
    () =>
      selectedFileUrl ? (
        <div className={style.card}>
          <div className={style.previewImageWrapper}>
            <IconRcCloseIcon
              className={style.previewCloseIcon}
              onClick={clearImage}
              width={32}
              height={32}
            />
            <img className={style.previewImage} src={selectedFileUrl} alt="" />
            {selectedFile?.id ? null : <Loading className={style.imageLoading} />}
          </div>
        </div>
      ) : null,
    [clearImage, selectedFile?.id, selectedFileUrl],
  );

  const handlePaste = useCallback(
    (e: any) => {
      if (!e.clipboardData) return false;
      const items = e.clipboardData.items;
      if (items && items.length) {
        for (let i = 0; i < items.length; i++) {
          if (items[i].type.indexOf('image') !== -1) {
            const file = items[i].getAsFile();
            if (file) {
              handleBeforeUpload(file as RcFile);
            }
            break;
          }
        }
      }
      return false;
    },
    [handleBeforeUpload],
  );

  return (
    <div className={style.container} onPaste={handlePaste}>
      <Textarea
        rows={messageInputLineCount}
        ref={inputRef}
        className={cx(
          style.textarea,
          messageInputLineCount < MAX_INPUT_LINE && style.scrollbarHidden,
        )}
        value={value}
        onChange={handleInputChange}
        placeholder={messageInputPlaceholder}
      />
      <Textarea
        rows={1}
        ref={inputHelperRef}
        className={cx(style.textarea, style.textareaHelper)}
        value={value}
        placeholder={messageInputPlaceholder}
      />
      {ImagePreviewComp}

      <div className={style.toolbar}>
        <MessageInputToolbar onEmojiSelect={onEmojiClick} onBeforeUpload={handleBeforeUpload} />

        <div className={style.toolbarRight}>
          <HelperTooltip title={'Full screen'} placement="top">
            <IconRcHiInputExtend className={style.expand} onClick={toggleInputModal} />
          </HelperTooltip>
          {SubmitButtonComponent}
        </div>
      </div>
      {ToolbarBottomTip}
      <Modal
        bodyOpenClassName={''}
        isOpen={on}
        overlayClassName={style.modalOverlay}
        className={cx(style.modalContainer, openInModal && style.openInModal)}
        ariaHideApp={false}
        parentSelector={getContainerWrapper}
        shouldFocusAfterRender={false}
        onRequestClose={toggleInputModal}>
        <div className={style.modalHeader}>
          <IconRcHiCloseModal className={style.closeIcon} onClick={toggleInputModal} />
        </div>
        <Textarea
          className={cx(style.textarea, style.textareaInModal, selectedFileUrl && style.hasImage)}
          value={value}
          ref={inputModalRef}
          onChange={value => handleInputChange(value || '')}
          placeholder={messageInputPlaceholder}
        />
        {ImagePreviewComp}
        <div className={style.modalFooter}>
          <MessageInputToolbar onEmojiSelect={onEmojiClick} onBeforeUpload={handleBeforeUpload} />
          <div className={style.modalFooterRight}>{SubmitButtonComponent}</div>
        </div>
        {ToolbarBottomTip}
      </Modal>
      {otherSideUserCardInfo && onOfferModal ? (
        <OfferModal
          otherSideUserCardInfo={otherSideUserCardInfo}
          price={price}
          isFree={isFree}
          freeMessageCount={messagePermission?.free_message_count}
          turnOff={turnOffOfferModal}
          submitOffer={onSubmitMessage}
        />
      ) : null}
    </div>
  );
};
